import { PropertyUnitStatus } from '../../../../enums/property.enums';
import { ShortUserModel } from '../../../Murabaha/models/short_user.model';
import MurabahaInvestorServices from '../../../Murabaha/services/murabha_investor.services';
import { TStatusChip } from '../../../Murabaha/Tasks/components/StatusChip';

interface IPropertyUnit {
  id: number;
  unit_number: string;
  property_id: number;
  owner_id: number;
  unit_price: number;
  unit_size: number;
  floor_number: number;
  description_ar: string;
  description_en: string;
  media_url: string;
  status: PropertyUnitStatus;
  created_at: Date;
  updated_by: number;
  last_updated_at: Date;
  //
  unit_owner: ShortUserModel; // Local field for display purposes, not stored in DB
}

export class PropertyUnitModel implements IPropertyUnit {
  id: number;
  unit_number: string;
  property_id: number;
  owner_id: number;
  unit_price: number;
  unit_size: number;
  floor_number: number;
  description_ar: string;
  description_en: string;
  media_url: string;
  status: PropertyUnitStatus;
  created_at: Date;
  updated_by: number;
  last_updated_at: Date;
  //
  unit_owner: ShortUserModel;

  constructor(data: Partial<IPropertyUnit>) {
    this.id = data.id ?? 0;
    this.unit_number = data.unit_number ?? '';
    this.property_id = data.property_id ?? 0;
    this.unit_price = data.unit_price ?? -1;
    this.unit_size = data.unit_size ?? -1;
    this.floor_number = data.floor_number ?? -1;
    this.description_ar = data.description_ar ?? '';
    this.description_en = data.description_en ?? '';
    this.media_url = data.media_url ?? '';
    this.status = data.status ?? PropertyUnitStatus.available_sale;
    this.created_at = data.created_at ?? new Date();
    this.updated_by = data.updated_by ?? 0;
    this.last_updated_at = data.last_updated_at ?? new Date();
    //
    this.unit_owner = data.unit_owner ?? new ShortUserModel({});
    this.owner_id = data.owner_id ?? this.unit_owner.id ?? 0;
  }

  /** From json to class instance */
  static fromJson(json: any): PropertyUnitModel {
    return new PropertyUnitModel({
      ...json,
      unit_owner: ShortUserModel.fromJson(json.unit_owner ?? {}),
    });
  }

  /** From class instance to json */
  toJson() {
    return {
      id: this.id,
      unit_number: this.unit_number,
      property_id: this.property_id,
      owner_id: this.owner_id,
      unit_price: this.unit_price,
      unit_size: this.unit_size,
      floor_number: this.floor_number,
      description_ar: this.description_ar,
      description_en: this.description_en,
      media_url: this.media_url,
      status: this.status,
      created_at: this.created_at,
      updated_by: this.updated_by,
      last_updated_at: this.last_updated_at,
      //
      unit_owner: this.unit_owner.toJson(),
    };
  }

  toUpdateJson() {
    return {
      id: this.id,
      unit_number: this.unit_number,
      property_id: this.property_id,
      owner_id: this.owner_id,
      unit_price: this.unit_price,
      unit_size: this.unit_size,
      floor_number: this.floor_number,
      description_ar: this.description_ar,
      description_en: this.description_en,
      media_url: this.media_url,
      status: this.status,
      owner: this.owner_id,
      property: this.property_id,
    };
  }

  /** `copyWith` to create a new instance with updated values */
  copyWith(data: Partial<IPropertyUnit>): PropertyUnitModel {
    return new PropertyUnitModel({ ...this, ...data });
  }

  get isValidSODto(): boolean {
    return (
      this.unit_number.trim() !== '' &&
      this.owner_id > 0 &&
      this.unit_owner.english_name.trim() !== '' &&
      this.unit_price >= 0 &&
      this.unit_size >= 0 &&
      // this.floor_number >= 0 &&
      this.description_ar.trim() !== '' &&
      this.description_en.trim() !== ''
    );
  }

  get isValidFODto(): boolean {
    return (
      this.owner_id > 0 &&
      this.unit_owner.english_name.trim() !== '' &&
      this.unit_size >= 0
    );
  }

  get isAvailableForSale(): boolean {
    return this.status === PropertyUnitStatus.available_sale;
  }

  get isReserved(): boolean {
    return this.status === PropertyUnitStatus.reserved;
  }

  get isSold(): boolean {
    return this.status === PropertyUnitStatus.sold;
  }

  get isHidden(): boolean {
    return this.status === PropertyUnitStatus.hidden;
  }

  /**
   * Create validation parser from excel data for below fields:
   * - unit_number: string, required, non-empty
   * - owner_id: number, required, positive integer
   * - unit_price: number, required, non-negative
   * - unit_size: number, required, non-negative
   * - floor_number: number, required, non-negative integer
   * - description_ar: string, required, non-empty
   * - description_en: string, required, non-empty
   * - media_url: string, optional, valid URL if provided
   * - status: string, required, valid values are 'available_sale', 'sold', 'reserved' (default to 'available_sale' if not provided)
   * And return the Property unit model instance if valid, or an error message if invalid with the field and field errors.
   */

  static async parseExcelData(
    data: any[],
    id: number,
    isArabicLocale: boolean
  ): Promise<{
    unit?: PropertyUnitModel;
    errors?: string[];
  }> {
    const errors: string[] = [];
    const unit_number = String(data[0] ?? '').trim();
    if (!unit_number) {
      errors.push(
        isArabicLocale
          ? 'رقم الوحدة مطلوب وما ينفع يكون فاضي'
          : 'unit_number is required and cannot be empty'
      );
    }

    const owner_id = Number(data[1]);
    let unit_owner = new ShortUserModel({});
    if (isNaN(owner_id) || owner_id <= 0) {
      errors.push(
        isArabicLocale
          ? 'رقم المالك مطلوب ولازم يكون رقم صحيح أكبر من صفر'
          : 'owner_id is required and must be a positive integer'
      );
    }
    // Check the owner with provided id
    else {
      const { isSuccess, data } = await MurabahaInvestorServices.getInvestorById(
        owner_id.toString()
      );
      if (isSuccess && data) unit_owner = data.shortUser;
      else
        errors.push(
          isArabicLocale
            ? `ما لقينا مستثمر برقم المالك ${owner_id}`
            : `No investor found with owner_id ${owner_id}`
        );
    }

    const unit_price = Number(data[2]);
    if (isNaN(unit_price) || unit_price < 0) {
      errors.push(
        isArabicLocale
          ? 'سعر الوحدة مطلوب ولازم يكون رقم غير سالب'
          : 'unit_price is required and must be a non-negative number'
      );
    }

    const unit_size = Number(data[3]);
    if (isNaN(unit_size) || unit_size < 0) {
      errors.push(
        isArabicLocale
          ? 'مساحة الوحدة مطلوبة ولازم تكون رقم غير سالب'
          : 'unit_size is required and must be a non-negative number'
      );
    }

    const floor_number = Number(data[4]);
    if (isNaN(floor_number) || floor_number < 0) {
      errors.push(
        isArabicLocale
          ? 'رقم الدور مطلوب ولازم يكون رقم صحيح غير سالب'
          : 'floor_number is required and must be a non-negative integer'
      );
    }

    const description_ar = String(data[5] ?? '').trim();
    if (!description_ar) {
      errors.push(
        isArabicLocale
          ? 'الوصف العربي مطلوب وما ينفع يكون فاضي'
          : 'description_ar is required and cannot be empty'
      );
    }

    const description_en = String(data[6] ?? '').trim();
    if (!description_en) {
      errors.push(
        isArabicLocale
          ? 'الوصف الإنجليزي مطلوب وما ينفع يكون فاضي'
          : 'description_en is required and cannot be empty'
      );
    }

    const media_url = String(data[7] ?? '').trim();
    if (media_url && !/^https?:\/\/\S+$/.test(media_url)) {
      errors.push(
        isArabicLocale
          ? 'رابط الوسائط إذا انكتب لازم يكون رابط صحيح'
          : 'media_url must be a valid URL if provided'
      );
    }

    const statusStr = String(data[8]);
    const status = statusMapping[statusStr] ?? PropertyUnitStatus.available_sale;
    if (!statusMapping[statusStr]) {
      errors.push(
        isArabicLocale
          ? "الحالة مطلوبة ولازم تكون واحدة من: 'متاح للبيع' أو 'مباع' أو 'محجوز' أو 'مخفي'"
          : "status is required and must be one of 'Available for Sale', 'Sold', 'Reserved', or 'Hidden'"
      );
    }

    if (errors.length > 0) {
      return { errors };
    }

    const unit = new PropertyUnitModel({
      id,
      unit_number,
      owner_id,
      unit_owner,
      unit_price,
      unit_size,
      floor_number,
      description_ar,
      description_en,
      media_url,
      status,
    });

    return { unit };
  }

  /** Get the status chip type */
  get statusChipType(): TStatusChip {
    return this.status === PropertyUnitStatus.available_sale
      ? 'COMPLETED'
      : this.status === PropertyUnitStatus.reserved
      ? 'IN_PROGRESS'
      : this.status === PropertyUnitStatus.sold
      ? 'CANCELLED'
      : 'PENDING';
  }

  get color() {
    switch (this.status) {
      case PropertyUnitStatus.available_sale:
        return 'text-greenBg';
      case PropertyUnitStatus.sold:
        return 'text-red-500';
      case PropertyUnitStatus.reserved:
        return 'text-yellowColor';
      default:
        return 'text-hintText';
    }
  }

  get borderBgColor() {
    switch (this.status) {
      case PropertyUnitStatus.available_sale:
        return {
          right: 'border-r-greenBg',
          left: 'border-l-greenBg',
        };

      case PropertyUnitStatus.sold:
        return {
          right: 'border-r-red-500',
          left: 'border-l-red-500',
        };

      case PropertyUnitStatus.reserved:
        return {
          right: 'border-r-yellowColor',
          left: 'border-l-yellowColor',
        };

      default:
        return {
          right: 'border-r-hintText',
          left: 'border-l-hintText',
        };
    }
  }
}
// Available for Sale
// Reserved
// Sold
// Hidden
const statusMapping: { [key: string]: PropertyUnitStatus } = {
  'Available for Sale': PropertyUnitStatus.available_sale,
  Reserved: PropertyUnitStatus.reserved,
  Sold: PropertyUnitStatus.sold,
  Hidden: PropertyUnitStatus.hidden,
};
